import React from 'react';
import { Input, withStyles } from '@material-ui/core';
import clsx from 'clsx';

import isEmailFn from './isEmail';
import useStyles from './styles';

/* eslint-disable react/destructuring-assignment, @typescript-eslint/no-explicit-any  */

export interface ReactMultiEmailProps {
  emails?: string[];
  onChange?: (emails: string[]) => void;
  onChangeInput?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  validateEmail?: (email: string) => boolean;
  style?: object;
  getLabel: (
    email: string,
    index: number,
    removeEmail: (index: number) => void,
  ) => void;
  className?: string;
  placeholder?: string | React.ReactNode;
  classes: any;
}

export interface ReactMultiEmailState {
  focused?: boolean;
  propsEmails?: string[];
  emails: string[];
  inputValue?: string;
}

class ReactMultiEmail extends React.Component<ReactMultiEmailProps, ReactMultiEmailState> {
  // eslint-disable-next-line react/state-in-constructor
  state = {
    focused: false,
    emails: [],
    inputValue: '',
  };

  emailInputRef: React.RefObject<HTMLInputElement>;

  constructor(props: ReactMultiEmailProps) {
    super(props);
    this.emailInputRef = React.createRef();
  }

  findEmailAddress = (value: string, isEnter?: boolean) => {
    const { validateEmail } = this.props;
    const validEmails: string[] = [];
    let inputValue: string = '';
    const re = /[\s,;]/g;
    const isEmail = validateEmail || isEmailFn;

    const addEmails = (email: string) => {
      const { emails } = this.state;
      for (let i = 0, l = emails.length; i < l; i++) {
        if (emails[i] === email) {
          return false;
        }
      }
      validEmails.push(email);
      return true;
    };

    if (value.trim() !== '') {
      if (re.test(value)) {
        const splitData = value.split(re).filter((n) => n !== '' && n !== undefined && n !== null);

        if (splitData.length === 2 && splitData[1] !== '' && isEmail(splitData.join(''))) {
          addEmails(splitData.join(''));
        } else if (isEmail(splitData[0])) {
          addEmails(splitData[0]);
        } else {
          inputValue = value;
        }
      } else if (isEnter) {
        if (isEmail(value)) {
          addEmails(value);
        } else {
          inputValue = value;
        }
      } else {
        inputValue = value;
      }
    }

    this.setState({
      emails: [...this.state.emails, ...validEmails],
      inputValue,
    });

    if (this.props.onChangeInput) {
      this.props.onChangeInput(inputValue);
    }

    if (validEmails.length && this.props.onChange) {
      this.props.onChange([...this.state.emails, ...validEmails]);
    }
  };

  onChangeInputValue = (value: string) => {
    this.findEmailAddress(value);
    setTimeout(
      () => this.handleOnFocus(),
      10,
    );
  };

  removeEmail = (index: number) => {
    this.setState(
      (prevState) => ({
        emails: [
          ...prevState.emails.slice(0, index),
          ...prevState.emails.slice(index + 1),
        ],
        inputValue: prevState.inputValue,
      }),
      () => {
        if (this.props.onChange) {
          this.props.onChange(this.state.emails);
        }
      },
    );
    setTimeout(
      () => this.handleOnFocus(),
      10,
    );
  };

  handleOnKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.which) {
      case 13:
      case 9:
        e.preventDefault();
        break;
      case 8:
        if (!e.currentTarget.value) {
          this.removeEmail(this.state.emails.length - 1);
        }
        break;
      default:
    }
  };

  handleOnKeyup = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.which) {
      case 13:
      case 9:
        this.findEmailAddress(e.currentTarget.value, true);
        break;
      default:
    }
  };

  handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.onChangeInputValue(e.currentTarget.value);
  };

  handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    this.setState({ focused: false });
    this.findEmailAddress(e.currentTarget.value, true);
  };

  handleOnFocus = () => this.setState({
    focused: true,
  });

  render() {
    const { focused, emails, inputValue } = this.state;
    const {
      classes, style, getLabel, className = '', placeholder, disabled = false, autoFocus = false,
    } = this.props;

    return (
      <div
        className={clsx(
          className,
          classes.root,
          focused ? classes.focused : '',
          (inputValue === '' && emails.length === 0) ? classes.empty : '',
        )}
        style={style}
        onClick={() => {
          if (this.emailInputRef.current) {
            this.emailInputRef.current.focus();
          }
        }}
      >
        {placeholder ? <span data-placeholder>{placeholder}</span> : null}
        {emails.map((email: string, index: number) => getLabel(email, index, this.removeEmail))}
        <Input
          fullWidth
          inputRef={this.emailInputRef}
          autoFocus={autoFocus}
          disabled={disabled}
          type="text"
          value={inputValue}
          onFocus={this.handleOnFocus}
          onBlur={this.handleOnBlur}
          onChange={this.handleOnChange}
          onKeyDown={this.handleOnKeydown}
          onKeyUp={this.handleOnKeyup}
          classes={{
            input: classes.input,
            underline: classes.inputUnderline,
            focused: classes.inputFocused,
            disabled: classes.disabled,
          }}
        />
      </div>
    );
  }
}

export default withStyles(useStyles)(ReactMultiEmail);
